import { Info } from 'lucide-react';
import type { AccountMetricsResponse } from '@pnl/shared';
import { Tooltip } from '../Tooltip';
import { Card } from './Card';

interface Props {
  score: AccountMetricsResponse['disciplineScore'];
}

const SIZE = 240;
const HEIGHT = 148;
const CX = SIZE / 2;
const CY = 124;
const R_OUT = 96;
const BAR_LEN = 13;
const R_IN = R_OUT - BAR_LEN;
const LABEL_R = R_OUT + 12;
const PAD = 18; // respiro lateral no SVG pra labels 0%/100% não cortarem
const N_TICKS = 80;
const GREEN = '#00C9A7';
/** Cinza do estado "ainda não operou" — mesmo tom dos labels do gauge. */
const NEUTRAL = '#8A8A8A';

// Stops do gradiente vermelho → laranja → amarelo → teal
const STOPS: Array<[number, [number, number, number]]> = [
  [0, [232, 64, 64]], // #E84040
  [0.3, [245, 158, 11]], // #F59E0B
  [0.55, [245, 197, 24]], // #F5C518
  [0.8, [0, 201, 177]], // #00C9B1
  [1, [0, 201, 167]], // #00C9A7
];

function colorAt(t: number): string {
  for (let i = 0; i < STOPS.length - 1; i += 1) {
    const [t0, c0] = STOPS[i]!;
    const [t1, c1] = STOPS[i + 1]!;
    if (t <= t1) {
      const f = t1 === t0 ? 0 : (t - t0) / (t1 - t0);
      const r = Math.round(c0[0] + (c1[0] - c0[0]) * f);
      const g = Math.round(c0[1] + (c1[1] - c0[1]) * f);
      const b = Math.round(c0[2] + (c1[2] - c0[2]) * f);
      return `rgb(${r},${g},${b})`;
    }
  }
  return GREEN;
}

/** Ponto na semicircunferência. t=0 → esquerda (0%), t=1 → direita (100%). */
function polar(t: number, r: number): { x: number; y: number } {
  const phi = Math.PI - t * Math.PI;
  return { x: CX + r * Math.cos(phi), y: CY - r * Math.sin(phi) };
}

const TICK_LABELS = [0, 30, 50, 80, 100];

export function DisciplineScore({ score }: Props) {
  const value = Math.max(0, Math.min(100, score.value));
  const ratio = value / 100;

  // Conta que ainda não operou não tem pontuação — tem AUSÊNCIA de pontuação.
  // Mostrar 0% em vermelho dava a entender que o trader foi mal, quando ele só
  // ainda não começou (pedido do cliente: tom neutro nesse estado).
  const idle = score.label === 'Sem operações';

  // Arco-íris completo (estilo FTMO): TODOS os ticks recebem a cor do gradiente.
  // Os ticks até o valor ficam acesos (com glow); os além ficam esmaecidos.
  // No estado ocioso o arco fica cinza e inteiro apagado, sem marcador.
  const bars = Array.from({ length: N_TICKS }, (_, i) => {
    const t = i / (N_TICKS - 1);
    const inner = polar(t, R_IN);
    const outer = polar(t, R_OUT);
    const active = !idle && t <= ratio + 1e-6;
    return { t, inner, outer, color: idle ? NEUTRAL : colorAt(t), active };
  });
  const markerColor = idle ? NEUTRAL : colorAt(ratio);
  const markerMid = polar(ratio, (R_IN + R_OUT) / 2);
  // Glow de fundo acompanha a cor da pontuação (vermelho→laranja→amarelo→verde).
  const glowColor = idle
    ? 'rgba(138,138,138,0.10)'
    : markerColor.replace('rgb(', 'rgba(').replace(')', ',0.28)');

  return (
    <Card
      className="border-transparent"
      title="Pontuação de Disciplina"
      action={
        <Tooltip label="Este valor calcula a consistência do seu trading com base no resultado de cada dia de trading. Um valor elevado indica um trading mais consistente.">
          <span className="border-ink-800 text-ink-400 grid h-7 w-7 place-items-center rounded-md border">
            <Info size={13} />
          </span>
        </Tooltip>
      }
    >
      <div className="flex flex-col items-center px-5 py-6">
        {/* Legenda topo */}
        <div className="text-2xs text-ink-400 mb-6 flex items-center justify-center gap-4">
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: '#E84040' }} />{' '}
            0 – 30%
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: '#F5C518' }} />{' '}
            30 – 80%
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: GREEN }} /> 80
            – 100%
          </span>
        </div>

        <div className="relative" style={{ width: SIZE + PAD * 2, height: HEIGHT }}>
          {/* Glow luminoso atrás do gauge — cor dinâmica conforme a pontuação. */}
          <div
            className="pointer-events-none absolute left-1/2 top-[46px] h-28 w-48 -translate-x-1/2 rounded-full blur-md"
            style={{
              background: `radial-gradient(ellipse at center, ${glowColor}, transparent 70%)`,
            }}
          />

          <svg
            width={SIZE + PAD * 2}
            height={HEIGHT}
            viewBox={`${-PAD} 0 ${SIZE + PAD * 2} ${HEIGHT}`}
            className="relative z-10"
          >
            <defs>
              <filter id="tick-glow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="2.2" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Ticks além do valor — coloridos, porém esmaecidos */}
            {bars
              .filter((b) => !b.active)
              .map((b, i) => (
                <line
                  key={`i-${i}`}
                  x1={b.inner.x}
                  y1={b.inner.y}
                  x2={b.outer.x}
                  y2={b.outer.y}
                  stroke={b.color}
                  strokeOpacity={0.28}
                  strokeWidth={2}
                  strokeLinecap="round"
                />
              ))}
            {/* Ticks até o valor — cor cheia + glow */}
            <g filter="url(#tick-glow)">
              {bars
                .filter((b) => b.active)
                .map((b, i) => (
                  <line
                    key={`a-${i}`}
                    x1={b.inner.x}
                    y1={b.inner.y}
                    x2={b.outer.x}
                    y2={b.outer.y}
                    stroke={b.color}
                    strokeWidth={2.4}
                    strokeLinecap="round"
                  />
                ))}
            </g>
            {/* Marcador da posição atual — escondido enquanto não há operações:
                um marcador no 0 (extremo vermelho) sugeriria pontuação péssima. */}
            {!idle && (
              <g filter="url(#tick-glow)">
                <circle
                  cx={markerMid.x}
                  cy={markerMid.y}
                  r={5}
                  fill={markerColor}
                  stroke="#FFFFFF"
                  strokeWidth={1.5}
                />
              </g>
            )}

            {/* Marcações 0 / 30 / 50 / 80 / 100 — todas no mesmo anel do arco.
                Endpoints (0/100) apontam pra fora; o PAD lateral evita corte. */}
            {TICK_LABELS.map((tk) => {
              const p = polar(tk / 100, LABEL_R);
              const anchor = tk === 0 ? 'end' : tk === 100 ? 'start' : 'middle';
              const x = tk === 80 ? p.x + 4 : p.x;
              return (
                <text
                  key={tk}
                  x={x}
                  y={p.y}
                  textAnchor={anchor}
                  fill="#8A8A8A"
                  fontSize="11"
                  dominantBaseline="middle"
                >
                  {tk}%
                </text>
              );
            })}
          </svg>

          {/* Valor central — número + label como unidade centrada no "bowl".
              Sem operações mostra "—": 0% seria lido como nota ruim, e o certo é
              "ainda não medido". */}
          <div className="absolute inset-x-0 top-[82px] z-10 text-center">
            <p
              className={
                'text-xl font-extrabold tabular-nums leading-none ' +
                (idle ? 'text-ink-400' : 'text-ink-100')
              }
            >
              {idle ? '—' : `${value}%`}
            </p>
            <p
              className={'mt-0.5 font-semibold leading-none ' + (idle ? 'text-xs' : 'text-[15px]')}
              style={{ color: markerColor }}
            >
              {idle ? 'Aguardando a primeira operação' : score.label}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}
